import { useMutation, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { Organization } from '@cast/types';
import { ORGANIZATIONS_QUERY_KEY } from '@cast/infrastructure-organization';

// Optimistically append the new org so downstream routes (e.g. /onboarding)
// can resolve it immediately without a full page refresh.
const appendOrganizationToCache = (
  organizations: Organization[] | undefined,
  organization: Organization
) => {
  const existing = organizations ?? [];

  if (existing.some((item) => item.id === organization.id)) {
    return existing;
  }

  return [...existing, organization];
};

export const useCreateOrganizationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Organization) =>
      apiClient.user.createOrganization({ organization: payload }),
    onSuccess: async ({ data: organization }) => {
      queryClient.setQueryData<Organization[]>(
        [ORGANIZATIONS_QUERY_KEY],
        (current) =>
          appendOrganizationToCache(current, organization as Organization)
      );

      await queryClient.refetchQueries({
        queryKey: [ORGANIZATIONS_QUERY_KEY],
      });
    },
    onError: (error: unknown) => {
      notify.error(parseApiError(error));
    },
  });
};
